import React from 'react';
import {useTranslation} from 'react-i18next';
import {useSnackbar} from 'notistack';
import {
  DialogTitle,
  DialogContent,
  DialogActions,
  Dialog,
  CircularProgress,
  Typography
} from '@mui/material';

import {APP} from 'src/styles/variables';
import SnackBarConfig from '@components/snackbar/SnackbarConfig';
import {getErrorMessage} from '@shared/utils/ErrorUtils';
import {softDeleteUser} from '@shared/services/service-handlers/PrivateService';
import {useAppContext} from '@context/AppContext';
import {LoadingButton} from '@mui/lab';

const UserGroupsDeleteDialog = (props: any): React.ReactElement => {
  const {t} = useTranslation();
  const {enqueueSnackbar} = useSnackbar();

  const {onClose, open, selectedUser, ...other} = props;
  const [loaders, setLoaders] = React.useState({
    componentLoading: false,
    buttonLoading: false
  });
  const {
    appState: {applicationName}
  } = useAppContext();

  const handleCancel = () => {
    onClose();
  };

  const handleSubmit = async () => {
    try {
      setLoaders(prevState => ({
        ...prevState,
        buttonLoading: true
      }));
      const res = await softDeleteUser(
        {data: selectedUser?.row.email},
        applicationName
      );
      enqueueSnackbar(res?.message, SnackBarConfig.getSuccess());

      setLoaders(prevState => ({
        ...prevState,
        buttonLoading: false
      }));
      handleCancel();
    } catch (error) {
      setLoaders(prevState => ({
        ...prevState,
        buttonLoading: false
      }));
      const errMessage = getErrorMessage(error);
      enqueueSnackbar(errMessage, SnackBarConfig.getError());
    }
  };

  return (
    <Dialog
      sx={{'& .MuiDialog-paper': {width: '80%', maxHeight: 435}}}
      maxWidth="xs"
      open={open}
      {...other}>
      <DialogTitle
        sx={{
          fontWeight: '700',
          color: APP.PALETTE.APP_BAR_COLOR.INFO_CHIP,
          fontSize: '1rem !important'
        }}>
        {t('PRIVATE.USER_GROUPS.DELETE_GROUPS', {ns: 'private'})}
      </DialogTitle>

      <DialogContent dividers>
        {loaders?.componentLoading ? (
          <div
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
            <CircularProgress size={30} />
          </div>
        ) : (
          <Typography>
            <p>
              Are you sure you want to delete this user{' '}
              {selectedUser.row.first_name}
            </p>
          </Typography>
        )}
      </DialogContent>

      <DialogActions>
        <LoadingButton
          type="submit"
          variant="outlined"
          disabled={loaders.buttonLoading}
          onClick={handleCancel}>
          {t('PRIVATE.ROLES.CANCEL', {ns: 'private'})}
        </LoadingButton>

        <LoadingButton
          type="submit"
          variant="contained"
          loading={loaders.buttonLoading}
          sx={{
            fontWeight: 'normal',
            backgroundColor: `${APP.PALETTE.MODULE_LAYOUT_COLOR.RED_BUTTON}`,
            '&:hover': {
              backgroundColor: `${APP.PALETTE.ROLES.RED}`
            }
          }}
          disabled={loaders.buttonLoading}
          onClick={handleSubmit}>
          {t('PRIVATE.ROLES.DELETE', {ns: 'private'})}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};
export default UserGroupsDeleteDialog;
